import { Component, inject, Input, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Dialog } from '@angular/cdk/dialog';
import { ModerationActionDialogComponent, ModerationActionDialogData } from './moderation-action-dialog.component';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoadingComponent } from './loading/loading.component';

interface ModerationCase {
  id: number; kind: string; source: string; targetId: number; subject: string;
  reason: string; comment?: string; imageUrl?: string; status: string; createdAt: string;
  updatedAt?: string; updatedBy?: string;
}

@Component({
  selector: 'app-moderation-inbox', standalone: true, imports: [CommonModule, RouterLink, TranslatePipe, LoadingComponent],
  template: `
    <section class="my-4">
      <h2>{{ 'MODERATION_INBOX' | translate }}</h2>
      <p>{{ 'MODERATION_24_HOURS' | translate }}</p>
      <div class="d-flex gap-2 flex-wrap">
        @for (status of ['PENDING', 'ACTION_TAKEN', 'DISMISSED']; track status) {
          <button type="button" class="btn btn-outline-primary" (click)="selected = status; load()">{{ status | translate }}</button>
        }
      </div>
      @if (showLoading && loading) { <div class="text-center my-3" role="status"><app-loading /></div> }
      @if (error) { <p role="alert">{{ error | translate }}</p> }
      @for (report of reports; track report.kind + report.id) {
        <article class="border rounded p-3 my-2 bg-dark text-light">
          <a [routerLink]="[(report.kind === 'car' ? '/cars' : report.kind === 'property' ? '/properties' : '/users'), report.targetId]">{{ report.subject }}</a>
          <p>{{ report.source }} · {{ report.reason | translate }} · {{ report.createdAt | date:'medium' }}</p>
          @if (overdue(report)) { <strong class="text-danger">{{ 'MODERATION_OVERDUE' | translate }}</strong> }
          <p>{{ report.comment }}</p>
          <p>{{ report.status | translate }}</p>
          @if (report.imageUrl) { <a [href]="report.imageUrl" target="_blank" rel="noopener noreferrer">{{ 'REPORT_IMAGE' | translate }}</a> }
          @if (report.updatedAt) { <p>{{ report.updatedAt | date:'medium' }} · {{ report.updatedBy }}</p> }
          <div class="d-flex gap-2 flex-wrap mt-2">
            @for (action of ['hide', 'suspend', 'resolve', 'dismiss', 'pending']; track action) {
              @if ((action !== 'pending' || report.status === 'ACTION_TAKEN' || report.status === 'DISMISSED')
                && (action !== 'resolve' || report.status !== 'ACTION_TAKEN')
                && (action !== 'dismiss' || report.status !== 'DISMISSED')) {
              <button type="button" class="btn btn-outline-primary" [disabled]="busy" (click)="act(report, action)">{{ ('MODERATION_' + action) | translate }}</button>
              }
            }
          </div>
        </article>
      }
      @if (reports.length < total) { <button type="button" class="btn btn-primary" [disabled]="loading || busy" (click)="load(true)">{{ 'MAP_LOAD_MORE' | translate }}</button> }
    </section>
  `,
})
export class ModerationInboxComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly dialogs = inject(Dialog);
  reports: ModerationCase[] = [];
  selected = 'PENDING'; total = 0; error = ''; busy = false;
  @Input() showLoading = true;
  loading = true;
  private loadVersion = 0;
  private readonly url = environment.apiUrl + '/api/moderation';
  ngOnInit(): void { void this.load(); }
  overdue(report: ModerationCase): boolean { return report.status === 'PENDING' && Date.now() - new Date(report.createdAt + (/[zZ]|[+-]\d\d:\d\d$/.test(report.createdAt) ? '' : 'Z')).getTime() >= 86400000; }
  async load(more = false): Promise<void> {
    if (more && this.loading) return;
    const version = ++this.loadVersion;
    this.loading = true;
    try {
      const result = await firstValueFrom(this.http.get<{ items: ModerationCase[]; total: number }>(this.url,
        { params: { status: this.selected, offset: more ? this.reports.length : 0, limit: 50 } }));
      if (version !== this.loadVersion) return;
      this.reports = more ? [...this.reports, ...result.items] : result.items; this.total = result.total; this.error = '';
    } catch { if (version === this.loadVersion) this.error = 'MODERATION_FAILED'; }
    finally { if (version === this.loadVersion) this.loading = false; }
  }
  async act(report: ModerationCase, action: string): Promise<boolean> {
    if (this.busy) return false;
    if (action === 'pending' && report.status !== 'ACTION_TAKEN' && report.status !== 'DISMISSED') return false;
    this.busy = true;
    try {
      const save = () => firstValueFrom(this.http.post(this.url + '/' + report.kind + '/' + report.id + '/' + action, {}));
      if (action === 'hide' || action === 'suspend' || action === 'pending') {
        const data: ModerationActionDialogData = { action, subject: report.subject, isUser: report.kind === 'user', save };
        const confirmed = await firstValueFrom(this.dialogs.open<boolean>(ModerationActionDialogComponent, {
          data, role: 'alertdialog', ariaLabelledBy: 'moderation-action-title', ariaDescribedBy: 'moderation-action-message',
          width: '420px', maxWidth: 'calc(100vw - 32px)', maxHeight: '90vh',
          autoFocus: 'first-tabbable', restoreFocus: true,
        }).closed);
        if (confirmed !== true) return false;
      } else {
        await save();
      }
      await this.load();
      return true;
    }
    catch (error) {
      this.error = error instanceof HttpErrorResponse && error.status === 405
        ? 'MODERATION_ENDPOINT_UNAVAILABLE' : 'MODERATION_FAILED';
      return false;
    }
    finally { this.busy = false; }
  }
}
